import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery, useLazyQuery, useMutation } from "react-apollo";
import { ChoiceList, Card, Layout, Link, Tabs, Tag, TextContainer } from "@shopify/polaris";
import { ImportMinor, AddProductMajor } from "@shopify/polaris-icons";

// import hoc
import { PrivateContext } from "lib/context";
import { withFeature, withErrorBoundary } from "lib/hoc";

// import form lib
import { baseHelper, errorHelper, storageHelper } from "lib/helpers";
import { useDebounce } from "lib/hooks";
import constant from "lib/constant/constant";

// layout component
import { SingleColumnLayout } from "layout/private/components";

// lib components
import { ResourceList, Banner, ModalPopup, PermissionBanner } from "lib/components";

// report components
import { QuickAction, QuickReport } from "app/reports";

// import GQL
import { GET_PRODUCT_LIST, GET_PRODUCT_LISTING, GET_PRODUCT_DIFF_COUNTS } from "app/productOld/apollo/queries";
import { GET_VENDOR_LIST_STATUS } from "app/vendors/apollo/queries";
import { BULK_PRODUCT } from "app/productOld/apollo/mutations";
import productListCMS from "app/productOld/modules/generic/feature/list/cms/productListCMS";
import UpgradAppPermission from "app/appPermission/modules/UpgradAppPermission";

// import subFeatures.
import { RejectProduct, DeleteProduct, ProductListView } from "app/productOld/modules/generic/feature/list";
import MoreFilters, { Filter, PRODUCT_DIFF_FIELD_CHOICES, StockFilter } from "app/productOld/modules/generic/moreFilters/moreFilter";
import { SUBSCRIPTION_BY_OPERATOR } from "app/productOld/apollo/subscriptions/productList";
import OperatorExportProduct from "app/productOld/modules/operator/features/export/export";
import { SyncProduct } from "./subFeature";

// The free text search is a regex over the whole product collection, so each
// request is expensive. Hold off until typing settles, and ignore terms short
// enough that they both scan everything and match nearly everything.
const SEARCH_DEBOUNCE_MS = 600;
const MIN_SEARCH_LENGTH = 3;

// A bulk vendor sync emits one subscription event per product; coalesce them
// into a single refetch.
const SUBSCRIPTION_REFETCH_DEBOUNCE_MS = 1500;

function parseProductStockFromQuery(queryParams) {
  const lf = (queryParams.list_filter && queryParams.list_filter.split(",")) || [];
  const ls = (queryParams.list_search && queryParams.list_search.split(",")) || [];
  const idx = lf.indexOf(constant.PRODUCT_STOCK);
  if (idx < 0) return [];
  const raw = ls[idx] || "";
  if (!raw) return [];
  return raw
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildOperatorListFilterSearch({ selectedFilter, taggedWith, productStock }) {
  const filters = [];
  const searches = [];
  if (selectedFilter) {
    filters.push(selectedFilter);
    searches.push(taggedWith || "");
  }
  const encoded = Array.isArray(productStock)
    ? productStock.map((s) => String(s).trim()).filter(Boolean).join("|")
    : "";
  if (encoded) {
    filters.push(constant.PRODUCT_STOCK);
    searches.push(encoded);
  }
  return {
    list_filter: filters.join(","),
    list_search: searches.join(","),
  };
}

function labelOneProductStock(value, cms) {
  if (value === constant.OUT_OF_STOCK) return cms("label.outOfStock") || "Out of stock";
  if (value === constant.IN_STOCK) return cms("label.inStock") || "In stock";
  return baseHelper.ucFirst(value);
}

const OperatorProductList = () => {
  const { cms, currentUser, history, isShopifyCustomOperator } = useContext(PrivateContext);
  const { fulfillmentScopeExists = false } = currentUser;
  const prevAppliedFilter = baseHelper.queryParamsFromLocation(history);
  const productStockFromUrl = parseProductStockFromQuery(prevAppliedFilter);
  // A stable primitive for the effect below - the parsed array is rebuilt every render.
  const productStockKey = productStockFromUrl.join("|");

  const queryParam = baseHelper.getQueryParams(history.location.search);

  const paramFilter = (prevAppliedFilter.filter && prevAppliedFilter.filter.split(",")) || [];
  const [filterByStatus, setFilterByStatus] = useState(paramFilter || []);

  const [selected, setSelected] = useState(parseInt(prevAppliedFilter.tab, 10) || 0);
  const selectedSort = prevAppliedFilter.sort_name && `${prevAppliedFilter.sort_name}_${prevAppliedFilter.sort_order}`;
  const [sortValue, setSortValue] = useState(selectedSort || constant.UPDATE_DESC);
  const [productList, setProductList] = useState(false);
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [selectedLimit, setSelectedLimit] = useState(parseInt(prevAppliedFilter.perPage, 10) || 10);
  const [selectedItems, setSelectedItems] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [currentPage, setCurrentPage] = useState(parseInt(prevAppliedFilter.page, 10) || 1);
  const [queryValue, setQueryValue] = useState(prevAppliedFilter.search ? decodeURI(prevAppliedFilter.search) : null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSyncModal, setOpenSyncModal] = useState(false);
  const [singleId, setSingleId] = useState(null);
  const [selectedButton, setSelectedButton] = useState(null);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [modalTitle, setModalTitle] = useState(null);
  const [primaryActionTitle, setPrimaryActionTitle] = useState(null);
  const [secondaryActionsTitle, setSecondaryActionsTitle] = useState(null);
  const [keyName, setKeyName] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(prevAppliedFilter.list_filter || "");
  const [taggedWith, setTaggedWith] = useState(prevAppliedFilter.list_search || "");
  const initProductDiff = prevAppliedFilter.list_filter === constant.PRODUCT_DIFF && prevAppliedFilter.list_search
    ? prevAppliedFilter.list_search.split("|").filter(Boolean)
    : [];
  const [productDiffSelected, setProductDiffSelected] = useState(initProductDiff);
  const [defaultBanner, setDefaultBanner] = useState(true);
  const [activePopover, setActivePopover] = useState({});
  const [active, setActive] = useState({});
  const { filterStatus, tabs, sortOptions, message, resourceName } = productListCMS(cms);
  const [deleteSelected, setDeleteSelected] = useState([constant.DELETE_SHOPIFY]);
  const [selectedTab, setSelectedTab] = useState("all");
  const [popoverDisable, setPopoverDisable] = useState(false);
  const [isOpenModal, setModal] = useState(false);
  const [isproductListSetting, setIsProductListSetting] = useState(false);
  const [appUpdate, setAppUpdate] = useState(storageHelper.get("customAppUpdate"));
  const [isOpen, setIsOpen] = useState(false);

  const [dataToFetch, setDataToFetch] = useState({
    search: prevAppliedFilter.search,
    list_filter: prevAppliedFilter.list_filter,
    list_search: prevAppliedFilter.list_search,
    filter: prevAppliedFilter.filter,
    perPage: selectedLimit,
    page: currentPage,
    sort_name: prevAppliedFilter.sort_name || constant.UPDATE,
    sort_order: prevAppliedFilter.sort_order || constant.DESC,
  });

  // The server only has to recount when the result set itself changes. Paging
  // through an unchanged set reuses the total already on screen. Tracked
  // explicitly rather than inferred from count === 0, which is a legitimate
  // total for a search that matches nothing.
  const [ignoreCount, setIgnoreCount] = useState(false);

  const applyFilterChange = useCallback((next) => {
    setIgnoreCount(false);
    setDataToFetch(next);
  }, []);

  useEffect(() => {
    const sort = sortValue.split("_");
    // list_filter and list_search carry the advance filter and the stock filter
    // together, so they are rebuilt the same way the URL builds them. Taking
    // selectedFilter / taggedWith alone dropped the stock selection from the
    // snapshot this page is restored from.
    const { list_filter: listFilter, list_search: listSearch } = buildOperatorListFilterSearch({
      selectedFilter: selectedFilter ? decodeURI(selectedFilter) : "",
      taggedWith:
        selectedFilter === constant.PRODUCT_DIFF
          ? productDiffSelected.join("|")
          : (taggedWith && decodeURI(taggedWith)) || "",
      productStock: productStockFromUrl,
    });
    const productSearch = {
      search: queryValue ? decodeURI(queryValue) : "",
      list_filter: listFilter,
      list_search: listSearch,
      sort_name: sort[0] || constant.UPDATE,
      sort_order: sort[1] || constant.DESC,
      page: parseInt(currentPage, 10),
      perPage: parseInt(selectedLimit, 10),
      filter: filterByStatus || [],
    };
    storageHelper.set("productSearch", `?${new URLSearchParams(productSearch).toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryValue,
    taggedWith,
    selectedFilter,
    sortValue,
    currentPage,
    selectedLimit,
    filterByStatus,
    productDiffSelected,
    productStockKey,
  ]);

  // eslint-disable-next-line no-unused-vars
  const [banner, setBanner] = useState({
    isOpen: true,
    title: message.defaultBanner.title,
    status: "info",
  });
  const [resBanner, setResBanner] = useState({
    isOpen: false,
    title: "",
    status: "",
    onDismiss: null,
  });
  const currentQueryParams = baseHelper.queryParamsFromLocation(history);
  const listFilterParam = currentQueryParams.list_filter;
  const listSearchParam = currentQueryParams.list_search;

  // Only the query variables are debounced - queryValue is still set
  // synchronously by handleQueryValueChange, so typing stays responsive.
  const rawSearch = (dataToFetch.search || "").trim();
  const debouncedSearch = useDebounce(rawSearch, SEARCH_DEBOUNCE_MS);
  // Clearing the box returns the list immediately rather than after the debounce.
  const searchTerm = !rawSearch || debouncedSearch.trim().length < MIN_SEARCH_LENGTH ? "" : debouncedSearch.trim();

  const inputData = useMemo(() => {
    const data = {
      search: searchTerm ? encodeURI(searchTerm) : "",
      list_filter: listFilterParam ? encodeURI(listFilterParam) : listFilterParam,
      list_search: listSearchParam ? encodeURI(listSearchParam) : listSearchParam,
      filter: filterByStatus || selectedTab,
      isIgnoreCount: ignoreCount,
      sort_name: dataToFetch.sort_name,
      sort_order: dataToFetch.sort_order,
      page: parseInt(dataToFetch.page, 10),
      perPage: parseInt(dataToFetch.perPage, 10),
    };

    // update value based on display name - Advance search
    if (constant.ADVANCE_MAP_FILTER && constant.ADVANCE_MAP_FILTER[data.list_filter]) {
      data.list_filter = constant.ADVANCE_MAP_FILTER[data.list_filter];
    }
    data.list_search =
      data.list_filter === constant.SHOPIFY_CATALOG_ID && data.list_search
        ? (prevAppliedFilter && prevAppliedFilter.list_cat_id) || dataToFetch.list_cat_id
        : data.list_search;

    return data;
    // prevAppliedFilter is rebuilt from the location on every render, so it is
    // read here but deliberately left out of the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, listFilterParam, listSearchParam, filterByStatus, selectedTab, ignoreCount, dataToFetch]);

  // GQL:Operations
  const {
    error: errorProductList,
    loading: loadingProductList,
    data: dataProductList,
    subscribeToMore,
    refetch,
  } = useQuery(GET_PRODUCT_LIST, {
    variables: {
      input: inputData,
    },
    // Per query override only - the global default stays "network-only". Showing
    // the cached page while the refetch runs removes the blank list on every
    // back-navigation and tab switch.
    fetchPolicy: "cache-and-network",
  });

  const { loading: loadingProductListing, data: dataProductSetting } = useQuery(GET_PRODUCT_LISTING);

  // An aggregation over the seller's whole catalogue that only the "needs review"
  // popover reads, so it is fired when that popover is opened rather than on
  // every list load and every page click.
  const [getProductDiffCounts, { data: productDiffCountsData }] = useLazyQuery(GET_PRODUCT_DIFF_COUNTS, {
    fetchPolicy: "cache-and-network",
  });
  const productDiffCounts = (productDiffCountsData && productDiffCountsData.getProductDiffCounts && productDiffCountsData.getProductDiffCounts.data) || [];

  useEffect(() => {
    if (selectedFilter === constant.PRODUCT_DIFF) {
      getProductDiffCounts();
    }
  }, [selectedFilter, getProductDiffCounts]);

  useEffect(() => {
    if (dataProductSetting) {
      const productistResponse = baseHelper.getResponseData(dataProductSetting, "getProductListingSetting");
      if (productistResponse) {
        const { isEnabled = {} } = productistResponse;
        if (isEnabled) {
          setIsProductListSetting(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProductSetting]);

  // The slim document: this page only reads _id, onHoliday and isInactive, while
  // the shared one pulls ~25 fields per vendor.
  const { error: errorVendorList, loading: loadingVendorList, data: dataVendorList } = useQuery(GET_VENDOR_LIST_STATUS, {
    fetchPolicy: "cache-and-network",
  });

  const [bulkProductActions, { loading: loadingBulkProduct }] = useMutation(BULK_PRODUCT, {
    refetchQueries: [
      {
        query: GET_PRODUCT_LIST,
        variables: {
          input: inputData,
        },
      },
    ],
  });

  useEffect(() => {
    if (message.defaultBanner.title && defaultBanner) {
      setBanner((preState) => ({
        ...preState,
        title: message.defaultBanner.title,
      }));
      setDefaultBanner(false);
    }
  }, [defaultBanner, message]);

  useEffect(() => {
    if (filterByStatus && filterByStatus.length) {
      baseHelper.setUrl(history, { filter: filterByStatus, page: 1 });
    }
    return baseHelper.setUrl(history, { filter: "", page: 1 });
  }, [filterByStatus, history]);

  const handleDeleteModal = (eventKey, id = false) => {
    if (eventKey === constant.DELETE.toLowerCase() && id) {
      setSingleId(id);
      setOpenDeleteModal(true);
      return;
    }
    setSingleId(null);
    setOpenDeleteModal(true);
  };
  const handleRejectModal = (eventKey, id = false) => {
    setSingleId(null);

    if (
      (eventKey === constant.REJECT ||
        eventKey === constant.UNREJECT ||
        eventKey === constant.UNDELETE ||
        eventKey === constant.RE_SUBMIT) &&
      id
    ) {
      setSingleId(id);
    }
    switch (eventKey) {
      case constant.REJECT:
        setModalTitle(cms("label.rejectProduct"));
        setPrimaryActionTitle(cms("label.reject"));
        setSecondaryActionsTitle(cms("label.notReject"));
        setKeyName(eventKey);
        setSelectedButton(eventKey);
        setOpenRejectModal(true);
        break;
      case constant.UNREJECT:
        setModalTitle(cms("label.unrejectProduct"));
        setPrimaryActionTitle(cms("label.unreject"));
        setSecondaryActionsTitle(cms("label.cancel"));
        setKeyName(eventKey);
        setSelectedButton(eventKey);
        setOpenRejectModal(true);
        break;
      case constant.UNDELETE:
        setModalTitle(cms("label.undeleteProduct"));
        setPrimaryActionTitle(cms("label.undelete"));
        setSecondaryActionsTitle(cms("label.cancel"));
        setKeyName(eventKey);
        setSelectedButton(eventKey);
        setOpenRejectModal(true);
        break;
      case constant.RE_SUBMIT:
        setModalTitle(cms("label.resubmitProduct"));
        setPrimaryActionTitle(cms("label.resubmit"));
        setSecondaryActionsTitle(cms("label.cancel"));
        setKeyName(eventKey);
        setSelectedButton(eventKey);
        setOpenRejectModal(true);
        break;
      default:
        break;
    }
  };

  const checkHoliday = (items) => {
    const onHolidayVendors = [];
    vendorList.forEach((vendor) => {
      if (vendor && vendor.onHoliday) {
        onHolidayVendors.push(vendor._id);
      }
    });
    const selectedVendorIds = [];
    items.forEach((value) => {
      const product = productList.find((productData) => productData.productId === value);
      if (product && !selectedVendorIds.includes(product.vendorId)) {
        selectedVendorIds.push(product.vendorId);
      }
    });
    const check = selectedVendorIds.every((vendorId) => onHolidayVendors.includes(vendorId));
    return check;
  };

  const checkVendorStatus = (items) => {
    const inactiveVendors = [];
    vendorList.forEach((vendor) => {
      if (vendor && vendor.isInactive) {
        inactiveVendors.push(vendor._id);
      }
    });
    const selectedVendorIds = [];
    items.forEach((value) => {
      const product = productList.find((productData) => productData.productId === value);
      if (product && !selectedVendorIds.includes(product.vendorId)) {
        selectedVendorIds.push(product.vendorId);
      }
    });
    const check = selectedVendorIds.every((vendorId) => inactiveVendors.includes(vendorId));
    return check;
  };

  const bulkAction = async (eventKey, deleteFromShopify = false, id = false, reason = "", buttonSelected = null) => {
    const { location } = history;
    const { pathname } = location;
    if (
      pathname.includes(constant.PRODUCT_URL) &&
      !pathname.includes(constant.PRODUCT_URL_SHOPIFY) &&
      eventKey === constant.BULK_EDIT
    ) {
      history.push(constant.PRODUCT_URL_EDIT, { selectedItems });
      return;
    }
    let formData = {};
    formData = {
      key: eventKey,
      ids: selectedItems,
      deleteFromShopify,
      reason,
    };
    let holiday = false;
    let inactiveVendor = false;
    if (eventKey === constant.PUBLISH && selectedItems.length) {
      holiday = checkHoliday(selectedItems);
      inactiveVendor = checkVendorStatus(selectedItems);
    }
    if (id) {
      if (eventKey === constant.PUBLISH) {
        holiday = checkHoliday([id]);
      }
      formData.ids = [id];
      setListLoading(`${eventKey}_${id}`);
    }
    setSelectedButton(buttonSelected);
    // The action moves products between statuses, so the total has to be
    // recomputed even if the page did not change.
    setIgnoreCount(false);
    await bulkProductActions({ variables: { input: { ...formData } } })
      .then((res) => {
        setListLoading(false);
        setSingleId(null);
        setSelectedButton(null);
        setOpenDeleteModal(false);
        setOpenRejectModal(false);
        setSelectedItems([]);
        setDeleteSelected([constant.DELETE_SHOPIFY]);
        setKeyName(null);
        if (res) {
          const responseData = baseHelper.getResponseData(res.data, constant.gql.BULK_PRODUCT);
          const responseDataError = baseHelper.getResponseError(res.data, constant.gql.BULK_PRODUCT);
          if (responseData) {
            let title = cms("common.message.success.background");
            if (holiday) {
              title = cms("label.markAvailable");
            }
            if (inactiveVendor) {
              title = cms("label.markProduct");
            }
            setResBanner({ isOpen: true, title, status: holiday || inactiveVendor ? constant.INFO : constant.SUCCESS });
          }
          if (responseDataError) {
            setResBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
          }
        }
      })
      .catch((exception) => {
        setListLoading(false);
        setSingleId(null);
        setSelectedButton(null);
        setOpenDeleteModal(false);
        setResBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };

  const removeTag = useCallback(
    (tag) => () => {
      setIgnoreCount(false);
      setFilterByStatus((previousTags) => previousTags.filter((previousTag) => previousTag !== tag));
    },
    []
  );

  const sortFilter = (selectedItem) => {
    const sort = selectedItem.split("_");
    if (sort && sort.length === 2) {
      setSortValue(selectedItem);
      baseHelper.setUrl(history, { sort_name: sort[0], sort_order: sort[1] });
      const filterApplied = baseHelper.queryParamsFromLocation(history);
      applyFilterChange({ ...dataToFetch, ...filterApplied });
    }
  };

  useEffect(() => {
    setSelectedItems([]);
    const productListResponseResponseData = baseHelper.getResponseData(dataProductList, constant.gql.GET_PRODUCT_LIST);
    const productListResponseResponseError = baseHelper.getResponseError(
      dataProductList,
      constant.gql.GET_PRODUCT_LIST
    );
    if (productListResponseResponseData) {
      setProductList(productListResponseResponseData.productList);
      // A response fetched with isIgnoreCount carries count 0, which is not a
      // total - keep the one already on screen.
      if (!ignoreCount) {
        setTotalProductCount(productListResponseResponseData.count);
      }
    }
    if (productListResponseResponseError) {
      setResBanner({ status: constant.CRITICAL, title: productListResponseResponseError, isOpen: true });
    }
    // ignoreCount is read as it stood when the response arrived; re-running this
    // on a flag change alone would re-apply a stale response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProductList]);

  useEffect(() => {
    setSelectedItems([]);
    const vendorListResponseData = baseHelper.getResponseData(dataVendorList, constant.gql.GET_VENDOR_LIST);
    const vendorListErrorData = baseHelper.getResponseError(dataVendorList, constant.gql.GET_VENDOR_LIST);
    if (vendorListResponseData) {
      const { vendors: vendorListData = [] } = vendorListResponseData;
      setVendorList(vendorListData);
    }
    if (vendorListErrorData) {
      setListLoading(false);
      setResBanner({ status: constant.CRITICAL, title: vendorListErrorData, isOpen: true });
    }
  }, [dataVendorList]);

  useEffect(() => {
    if (errorProductList) {
      setListLoading(false);
      setResBanner({ status: constant.CRITICAL, title: errorHelper.parse(errorProductList), isOpen: true });
    }
    if (errorVendorList) {
      setResBanner({ status: constant.CRITICAL, title: errorHelper.parse(errorVendorList), isOpen: true });
    }
  }, [errorProductList, errorVendorList]);

  // Subscription for provider list Page.
  // A bulk vendor sync fires one event per product, so the refetch is coalesced -
  // otherwise N events meant N full list queries.
  const refetchTimer = useRef(null);
  useEffect(() => () => clearTimeout(refetchTimer.current), []);

  useEffect(() => {
    subscribeToMore({
      document: SUBSCRIPTION_BY_OPERATOR,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        clearTimeout(refetchTimer.current);
        refetchTimer.current = setTimeout(() => refetch(), SUBSCRIPTION_REFETCH_DEBOUNCE_MS);
        return prev;
      },
    });
  }, [subscribeToMore, refetch]);

  const handleSelectLimit = (value) => {
    setSelectedLimit(parseInt(value, 10));
    baseHelper.setUrl(history, { perPage: value });
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
  };

  const handlePage = (page) => {
    setCurrentPage(parseInt(page, 10));
    baseHelper.setUrl(history, { page });
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    // Only the page moved, so the result set - and its total - is unchanged.
    setIgnoreCount(true);
    setDataToFetch({ ...dataToFetch, ...filterApplied });
  };

  const handleQueryValueChange = (value) => {
    setQueryValue(value);
    baseHelper.setUrl(history, { search: value, page: 1 });
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
  };
  const handleQueryValueRemove = () => {
    setQueryValue(null);
    baseHelper.setUrl(history, { search: "" });
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
  };
  const handleTaggedWithRemove = (clearFilter = false, key) => {
    const clearParams =
      key === constant.FILTER_BY_STATUS ? { list_filter: "", page: 1 } : { list_cat_id: "", list_search: "", page: 1 };
    if (clearFilter) {
      // clearParams.list_filter = "";
      if (key) {
        switch (key) {
          case constant.TAGGED_WITH: {
            setTaggedWith("");
            baseHelper.setUrl(history, clearParams);
            const filterApplied = baseHelper.queryParamsFromLocation(history);
            return applyFilterChange({ ...dataToFetch, ...filterApplied });
          }
          case constant.FILTER_BY_STATUS:
            return [setFilterByStatus(null), baseHelper.setQueryParams(history.location, clearParams)];
          case constant.CLEAR_ALL:
            return [
              setTaggedWith(""),
              setSelectedFilter(""),
              setFilterByStatus(null),
              baseHelper.setUrl(history, clearParams),
            ];
          default:
            return null;
        }
      }
    }
    if (key === constant.FILTER_BY_STATUS) {
      setFilterByStatus(null);
    } else {
      // clearParams.list_filter = "";
      setTaggedWith("");
    }
    baseHelper.setUrl(history, clearParams);
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
    return null;
  };

  const handleSelectedFilterRemove = () => {
    setSelectedFilter("");
    setTaggedWith("");
    setProductDiffSelected([]);
    const productStock = parseProductStockFromQuery(baseHelper.queryParamsFromLocation(history));
    const { list_filter: lf, list_search: ls } = buildOperatorListFilterSearch({
      selectedFilter: "",
      taggedWith: "",
      productStock,
    });
    baseHelper.setUrl(history, { list_cat_id: "", list_search: ls, list_filter: lf, page: 1 });
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
  };

  const handleProductDiffChange = (values) => {
    setProductDiffSelected(values);
    const encoded = values.join("|");
    const productStock = parseProductStockFromQuery(baseHelper.queryParamsFromLocation(history));
    const { list_filter: lf, list_search: ls } = buildOperatorListFilterSearch({
      selectedFilter: constant.PRODUCT_DIFF,
      taggedWith: encoded,
      productStock,
    });
    const search = baseHelper.setQueryParams(history.location, { list_filter: lf, list_search: ls, page: 1 });
    history.push(`${history.location.pathname}?${search}`);
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
  };

  const handleProductStockChange = (value) => {
    const next = Array.isArray(value) ? value.map((s) => String(s).trim()).filter(Boolean) : [];
    const activeFilter = selectedFilter === constant.PRODUCT_DIFF
      ? constant.PRODUCT_DIFF
      : selectedFilter;
    const activeSearch = selectedFilter === constant.PRODUCT_DIFF
      ? productDiffSelected.join("|")
      : taggedWith;
    const { list_filter: lf, list_search: ls } = buildOperatorListFilterSearch({
      selectedFilter: activeFilter,
      taggedWith: activeSearch,
      productStock: next,
    });
    const search = baseHelper.setQueryParams(history.location, { list_filter: lf, list_search: ls, page: 1 });
    history.push(`${history.location.pathname}?${search}`);
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
  };

  const handleProductStockRemove = () => {
    handleProductStockChange([]);
  };

  const handleClearAll = () => {
    handleQueryValueRemove();
    setSelectedFilter("");
    setTaggedWith("");
    setFilterByStatus(null);
    setProductDiffSelected([]);
    baseHelper.setUrl(history, {
      list_cat_id: "",
      list_search: "",
      list_filter: "",
      page: 1,
    });
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    applyFilterChange({ ...dataToFetch, ...filterApplied });
  };

  let tagMarkup = [];
  if (filterByStatus && filterByStatus.length) {
    tagMarkup =
      filterByStatus &&
      filterByStatus.map((option) => (
        <Tag key={option} onRemove={removeTag(option)}>
          {option === constant.PENDING_SMALL ? cms("label.review") : baseHelper.ucFirst(option)}
        </Tag>
      ));
  }

  const removeProductStockTag = (tag) => () => {
    const next = productStockFromUrl.filter((stock) => stock !== tag);
    handleProductStockChange(next);
  };

  let productStockTagMarkup = [];
  if (productStockFromUrl && productStockFromUrl.length) {
    productStockTagMarkup = productStockFromUrl.map((option) => (
      <Tag key={option} onRemove={removeProductStockTag(option)}>
        {labelOneProductStock(option, cms)}
      </Tag>
    ));
  }

  const productDiffLabelMap = PRODUCT_DIFF_FIELD_CHOICES.reduce((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});

  function disambiguateLabel(key, value) {
    switch (key) {
      case constant.TAGGED_WITH:
        return `${value}`;
      case constant.SELECTED_FILTER:
        if (value === constant.PRODUCT_DIFF) return "Review Fields";
        return value === constant.SKU
          ? value.toUpperCase()
          : cms(`label.${value === constant.TITLE ? "titleName" : baseHelper.ucFirst(value)}`) ||
              baseHelper.ucFirst(value);
      case constant.FILTER_BY_STATUS:
        return <div className="remove_tag">{tagMarkup}</div>;
      case constant.FILTER_BY_STOCK:
        return <div className="remove_tag">{productStockTagMarkup}</div>;
      default:
        return value;
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  }
  const appliedFilters = [];

  const isProductDiffFilter = selectedFilter === constant.PRODUCT_DIFF;

  if (!isEmpty(taggedWith) && !isProductDiffFilter) {
    appliedFilters.push({
      key: "taggedWith",
      label: disambiguateLabel(constant.TAGGED_WITH, taggedWith),
      onRemove: () => handleTaggedWithRemove(true, constant.TAGGED_WITH),
    });
  }

  if (!isEmpty(selectedFilter) && !isProductDiffFilter) {
    appliedFilters.push({
      key: "selectedFilter",
      label: disambiguateLabel(constant.SELECTED_FILTER, selectedFilter),
      onRemove: handleSelectedFilterRemove,
    });
  }

  if (isProductDiffFilter) {
    if (!isEmpty(productDiffSelected)) {
      // One removable tag per selected diff field
      productDiffSelected.forEach((diffKey) => {
        appliedFilters.push({
          key: `productDiff_${diffKey}`,
          label: `Review Fields: ${productDiffLabelMap[diffKey] || baseHelper.ucFirst(diffKey)}`,
          onRemove: () => {
            const updated = productDiffSelected.filter((k) => k !== diffKey);
            handleProductDiffChange(updated);
            if (!updated.length) handleSelectedFilterRemove();
          },
        });
      });
    } else {
      // Filter type selected but no fields chosen yet — still show a clearable tag
      appliedFilters.push({
        key: "selectedFilter",
        label: "Review Fields",
        onRemove: handleSelectedFilterRemove,
      });
    }
  }

  if (!isEmpty(filterByStatus)) {
    appliedFilters.push({
      key: constant.FILTER_BY_STATUS,
      label: disambiguateLabel(constant.FILTER_BY_STATUS, filterByStatus),
      onRemove: () => handleTaggedWithRemove(true, constant.FILTER_BY_STATUS),
    });
  }

  if (!isEmpty(productStockFromUrl)) {
    appliedFilters.push({
      key: constant.FILTER_BY_STOCK,
      label: disambiguateLabel(constant.FILTER_BY_STOCK, productStockFromUrl),
      onRemove: handleProductStockRemove,
    });
  }

  const handleAction = (value) => {
    setIgnoreCount(false);
    setFilterByStatus(value);
    baseHelper.setUrl(history, { filter: value, page: 1 });
    if (!value.length) {
      baseHelper.setUrl(history, { filter: "all", page: 1 });
    }
  };

  const filters = [
    {
      key: "selectedFilter",
      label: cms("label.filterBy"),
      filter: (
        <Filter
          dataToFetch={dataToFetch}
          setDataToFetch={applyFilterChange}
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          productList={productList}
        />
      ),
      shortcut: false,
    },
    {
      key: constant.FILTER_BY_STATUS,
      label: cms("common.label.filterByStatus"),
      filter: (
        <ChoiceList
          title={cms("common.label.filterByStatus")}
          titleHidden
          choices={filterStatus}
          selected={filterByStatus || []}
          onChange={(value) => handleAction(value)}
          allowMultiple
        />
      ),
    },
    {
      key: constant.FILTER_BY_STOCK,
      label: cms("common.label.filterByStock") || "Filter by stock",
      filter: (
        <StockFilter
          selected={productStockFromUrl}
          onChange={handleProductStockChange}
        />
      ),
    },
  ];

  if (selectedFilter) {
    filters.push({
      key: "taggedWith",
      label: cms("label.filter"),
      filter: (
        <MoreFilters
          dataToFetch={dataToFetch}
          setDataToFetch={applyFilterChange}
          taggedWith={taggedWith}
          setTaggedWith={setTaggedWith}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          productDiffSelected={productDiffSelected}
          onProductDiffChange={handleProductDiffChange}
          productDiffCounts={productDiffCounts}
        />
      ),
      shortcut: false,
    });
  }

  const renderItem = (item, _, keyIndex) => {
    return (
      <ProductListView
        cms={cms}
        item={item}
        listLoading={listLoading}
        keyIndex={keyIndex}
        bulkAction={bulkAction}
        loadingBulkProduct={loadingBulkProduct}
        handleRejectModal={handleRejectModal}
        handleDeleteModal={handleDeleteModal}
        activePopover={activePopover}
        setActivePopover={setActivePopover}
        setListBanner={setResBanner}
        active={active}
        popoverDisable={popoverDisable}
        setActive={setActive}
        isproductListSetting={isproductListSetting}
      />
    );
  };

  const promotedBulkActions = [
    {
      content: cms("label.action"),
    },
  ];

  const bulkActions = [
    {
      content: cms("label.push"),
      onAction: () => bulkAction(constant.APPROVE),
      disabled: tabs[selected].value === constant.APPROVED.toLowerCase(),
    },
    {
      content: cms("label.deleteSelected"),
      onAction: () => handleDeleteModal(constant.DELETE.toLowerCase()),
      destructive: true,
      disabled:
        tabs[selected].value === constant.DELETED.toLowerCase() ||
        tabs[selected].value === constant.REJECTED.toLowerCase(),
    },
    {
      content: cms("label.edit"),
      onAction: () => bulkAction(constant.BULK_EDIT),
      disabled:
        tabs[selected].value === constant.DELETED.toLowerCase() ||
        tabs[selected].value === constant.REJECTED.toLowerCase(),
    },
    {
      content: cms("label.rejectSelected"),
      onAction: () => handleRejectModal(constant.REJECT),
      destructive: true,
      disabled:
        tabs[selected].value === constant.DELETED.toLowerCase() ||
        tabs[selected].value === constant.REJECTED.toLowerCase(),
    },
    {
      content: cms("label.available"),
      onAction: () => bulkAction(constant.PUBLISH),
    },
    {
      content: cms("label.unavailable"),
      onAction: () => bulkAction(constant.UNPUBLISH),
    },
    {
      content: cms("label.unrejectSelected"),
      onAction: () => handleRejectModal(constant.UNREJECT),
    },
  ];

  const linkData = [
    {
      action: () => history.push("/products/add"),
      caption: cms("section.caption.add"),
      id: "addProduct",
      primary: true,
      label: cms("label.add"),
      // icon: AddProductMajor,
    },
    {
      action: () => history.push("/products/edit"),
      caption: cms("section.caption.edit"),
      id: "editProducts",
      primary: true,
      label: cms("label.editProduct"),
    },
    {
      action: () => setModal(true),
      caption: cms("label.caption"),
      id: "exportPayment",
      primary: true,
      label: cms("section.export.label.title"),
      //  icon: ExportMinor,
    },
    {
      action: () => setOpenSyncModal(true),
      caption: cms("section.caption.import"),
      id: "importProduct",
      label: cms("label.import"),
      // icon: ImportMinor,
    },
  ];

  const updatedResourceName = { ...resourceName };

  updatedResourceName.singular = `${updatedResourceName.singular} from page ${currentPage}`;
  updatedResourceName.plural = `${updatedResourceName.plural} from page ${currentPage}`;

  const handleClose = () => {
    // setAppUpdate(!appUpdate);
    // storageHelper.set("customAppUpdate", "No");
    storageHelper.set("customAppUpdate", "updated");
    setIsOpen(false);
  };

  const getComponent = () => {
    return (
      <>
        {!isShopifyCustomOperator && appUpdate === "updated" && (
          <>
            <Banner
              isOpen
              action={{ content: "Migrate to Custom App", onAction: () => setIsOpen(true) }}
              title={cms("common.popup.migrate.title")}
              status="critical"
            >
              <p>{cms("common.popup.migrate.description")}</p>
            </Banner>
            <ModalPopup open={isOpen} onClose={handleClose} title="Upgrade to App permission">
              <Card>
                <TextContainer>
                  <Banner
                    isOpen
                    status="critical"
                    action={{
                      content: `${cms("common.appPermission.label.aboutCustom")}`,
                      url: constant.ABOUT_OPERATOR_APP_MIGRATE,
                    }}
                  >
                    <TextContainer>{cms("common.popup.migrate.description")}</TextContainer>
                  </Banner>
                </TextContainer>
              </Card>
              <UpgradAppPermission />
            </ModalPopup>
            <br />
          </>
        )}
        {/* {isShopifyCustomOperator && !fulfillmentScopeExists && <PermissionBanner />} */}
        <br />
        {resBanner.isOpen && (
          <>
            <Banner
              isOpen={resBanner.isOpen}
              title={resBanner.title}
              status={resBanner.status}
              onDismiss={() => {
                setResBanner({ isOpen: false, title: "", status: "" });
              }}
            />
            <br />
          </>
        )}
        <Banner isOpen title={cms("label.title")} status={constant.INFO} />
        <br />
        <QuickReport />
        <QuickAction link={linkData} />
        <Layout>
          <SyncProduct
            cms={cms}
            openSyncModal={openSyncModal}
            setResBanner={setResBanner}
            setOpenSyncModal={setOpenSyncModal}
            dataToFetch={inputData}
          />
          <DeleteProduct
            cms={cms}
            openDeleteModal={openDeleteModal}
            setOpenDeleteModal={setOpenDeleteModal}
            bulkAction={bulkAction}
            id={singleId}
            loadingBulkProduct={loadingBulkProduct}
            selectedButton={selectedButton}
            selectedItems={selectedItems}
            isSeller
            deleteSelected={deleteSelected}
          />
          <RejectProduct
            cms={cms}
            openRejectModal={openRejectModal}
            setOpenRejectModal={setOpenRejectModal}
            bulkAction={bulkAction}
            id={singleId}
            loadingBulkProduct={loadingBulkProduct}
            selectedButton={selectedButton}
            modalTitle={modalTitle}
            primaryActionTitle={primaryActionTitle}
            secondaryActionsTitle={secondaryActionsTitle}
            keyName={keyName}
          />
          <Layout.Section>
            <Card>
              <ResourceList
                resourceName={updatedResourceName}
                items={productList || []}
                idForItem={(products) => {
                  const { productId: id } = products;
                  return id;
                }}
                renderItem={renderItem}
                onQueryChange={handleQueryValueChange}
                onQueryClear={handleQueryValueRemove}
                handleClearAll={handleClearAll}
                queryValue={queryValue}
                selectedItems={selectedItems}
                onSelectionChange={setSelectedItems}
                sortValue={sortValue}
                sortOptions={sortOptions}
                onSortChange={sortFilter}
                bulkActions={bulkActions}
                promotedBulkActions={promotedBulkActions}
                loading={loadingProductList || loadingVendorList || loadingProductListing}
                filters={filters}
                appliedFilters={appliedFilters}
                page={currentPage}
                count={Number(totalProductCount)}
                perPage={selectedLimit}
                setPage={handlePage}
                setPopoverDisable={setPopoverDisable}
                setPerPage={handleSelectLimit}
              />
            </Card>
          </Layout.Section>
          <OperatorExportProduct vendorData={vendorList} openModal={isOpenModal} setOpenModal={setModal} />
        </Layout>
      </>
    );
  };

  return <SingleColumnLayout primary={getComponent()} />;
};

export default withFeature(withErrorBoundary(OperatorProductList), { feature: constant.PRODUCTS });
